import React, { useEffect, useState, useRef, useCallback } from "react";
import { LeftOutlined, RightOutlined, CloseOutlined } from "@ant-design/icons";

const NAV_BTN_STYLE = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 10,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 8,
  padding: "10px 14px",
  color: "#fff",
  fontSize: 14,
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  transition: "background 0.2s, opacity 0.2s",
  userSelect: "none",
};

const ImagePreviewModal = ({ visible, images, initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    if (visible) {
      setIndex(initialIndex);
      setImgLoaded(false);
    }
  }, [visible, initialIndex]);

  useEffect(() => {
    setImgLoaded(false);
  }, [index]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(images.length - 1, i + 1));
  }, [images.length]);

  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [visible, onClose, goPrev, goNext]);

  useEffect(() => {
    if (visible) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [visible]);

  if (!visible || !images?.length) return null;

  const currentSrc = typeof images[index] === "string" ? images[index] : images[index]?.url || images[index]?.src;
  const isFirst = index === 0;
  const isLast = index === images.length - 1;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1050,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.65)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "85vw", maxWidth: 1400, height: "80vh",
          display: "flex", flexDirection: "column",
          background: "#fff", borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 20px 0", position: "relative" }}>
          <div style={{ fontSize: 14, color: "#666", fontWeight: 500 }}>
            Image {index + 1} of {images.length}
          </div>
          <button
            onClick={onClose}
            style={{
              position: "absolute", right: 12, top: 8,
              background: "none", border: "none", cursor: "pointer",
              fontSize: 18, color: "#888", padding: 4, lineHeight: 1,
            }}
          >
            <CloseOutlined />
          </button>
        </div>

        <div
          style={{
            flex: 1, margin: "8px 16px 16px",
            background: "#f4f4f5", borderRadius: 12,
            position: "relative", overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <button
            onClick={goPrev}
            disabled={isFirst}
            style={{
              ...NAV_BTN_STYLE, left: 12,
              opacity: isFirst ? 0.35 : 1,
              cursor: isFirst ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => { if (!isFirst) e.currentTarget.style.background = "rgba(0,0,0,0.75)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.55)"; }}
          >
            <LeftOutlined /> Previous
          </button>

          <img
            ref={imgRef}
            src={currentSrc}
            alt=""
            draggable={false}
            onLoad={() => setImgLoaded(true)}
            style={{
              maxWidth: "100%", maxHeight: "100%",
              objectFit: "contain",
              borderRadius: 8,
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 0.25s ease, transform 0.25s ease",
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              transform: imgLoaded ? "scale(1)" : "scale(0.97)",
            }}
          />

          <button
            onClick={goNext}
            disabled={isLast}
            style={{
              ...NAV_BTN_STYLE, right: 12,
              opacity: isLast ? 0.35 : 1,
              cursor: isLast ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => { if (!isLast) e.currentTarget.style.background = "rgba(0,0,0,0.75)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.55)"; }}
          >
            Next <RightOutlined />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
